import * as React from "react";
import { useMemo } from "react";
import { BaseModal } from "../../../../atoms/base-modal";
import { RequestPayment } from "../../../../organisms/smart-apply/payment/request-payment/request-payment";
import { RequestPaymentWarningMessages } from "../../../../organisms/smart-apply/payment/request-payment/request-payment-warning-messages";
import { matchType } from "../../../../../application/domain/base/base-type";
import { useState } from "react";
import { useRequestPayment } from "../../../../organisms/smart-apply/payment/request-payment/hooks";
import { PaymentDetail } from "../../../../organisms/smart-apply/payment/payment-detail/payment-detail";
import {
  MaxPaymentAmount,
  PaymentMethodUILabel,
} from "@graffer-inc/cloud-gov-core";
import { CalcRequestType } from "../../../../../application/domain/payment/calc-request";
import { useSubmitting } from "../../../../../utils/custom-hooks";
import classNames from "classnames";
import { useAuthContextV2 } from "../../../../../auth-state";

type NewPostPaymentStepLiterals = "INPUT" | "CONFIRM";

export const AddNewPayment: React.FC<{
  onClose: () => void;
  onAddPayment: (calcReq: CalcRequestType) => Promise<boolean>;
}> = ({ onClose, onAddPayment }) => {
  const auth = useAuthContextV2();
  const { isSubmitting, stopSubmitting, startSubmitting } = useSubmitting();
  const [step, setStep] = useState<NewPostPaymentStepLiterals>("INPUT");
  const { calcRequest, setCalcRequest, paymentResult, paymentCalcState } =
    useRequestPayment();

  const isAcceptMultiMethodsOrg: boolean = useMemo(() => {
    if (auth) {
      return auth.auth.acceptedPaymentMethods.length > 1;
    }
    return false;
  }, [auth]);

  return (
    <BaseModal
      isActive={true}
      title={matchType(step, {
        INPUT: "支払い依頼の作成",
        CONFIRM: "支払い依頼の確認",
      })}
      onClose={onClose}
    >
      {matchType(step, {
        INPUT: (
          <>
            <section className="modal-card-body">
              <RequestPayment
                orgAcceptedPaymentMethods={auth.auth.acceptedPaymentMethods}
                isAcceptMultiMethodsOrg={isAcceptMultiMethodsOrg}
                calcRequest={calcRequest}
                setCalcRequest={setCalcRequest}
                paymentCalcState={paymentCalcState}
                paymentResult={paymentResult}
              />
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={() => onClose()}
              >
                キャンセル
              </button>
              <button
                className="button is-info is-fullwidth"
                onClick={() => setStep("CONFIRM")}
                disabled={
                  paymentCalcState.step !== "success" ||
                  (!!paymentResult.payment &&
                    paymentResult.payment?.amount > MaxPaymentAmount)
                }
              >
                内容の確認へ進む
              </button>
            </footer>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <PaymentDetail payment={paymentResult.payment!} />
              {isAcceptMultiMethodsOrg && (
                <div className="field">
                  <label className="label">支払い手段</label>
                  {calcRequest.getValue().acceptedPaymentMethods.map((m) => (
                    <p key={m}>{PaymentMethodUILabel[m]}</p>
                  ))}
                </div>
              )}
              {calcRequest.getValue().message && (
                <div className="field">
                  <label className="label">申請者へのメッセージ</label>
                  <p
                    style={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {calcRequest.getValue().message}
                  </p>
                </div>
              )}

              <RequestPaymentWarningMessages
                calcRequest={calcRequest}
                paymentAmount={paymentResult.payment?.amount}
              />
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={() => onClose()}
              >
                キャンセル
              </button>
              <button
                className={classNames("button is-info is-fullwidth", {
                  "is-loading": isSubmitting,
                })}
                onClick={async () => {
                  startSubmitting();
                  if (await onAddPayment(calcRequest.getValue())) {
                    onClose();
                  }
                  stopSubmitting();
                }}
                disabled={
                  paymentCalcState.step !== "success" ||
                  (!!paymentResult.payment &&
                    paymentResult.payment?.amount > MaxPaymentAmount)
                }
              >
                この内容で登録する
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
